import type { ForwardedRef } from 'react';
import type { DialogSessionProps } from '@ricoh/ria.client.core/components/dialog/session';

import { useCallback, useImperativeHandle, useMemo } from 'react';
import { useLoadingScreen } from '@ricoh/ria.client.core/hooks/component/loading';
import useSwrMutation from '@ricoh/ria.client.core/plugins/swr/hooks/mutation';

import { getUserFetcher, signoutFetcher } from '~/services/server/identity/account';
import { authenticator } from '~/plugins/auth';

import type { SessionTimeoutHooks, SessionTimeoutProps, SessionTimeoutRef } from './_.type';

/**
 * Use SessionTimeout hooks.
 *
 * @param {SessionTimeoutProps} _props SessionTimeout props.
 * @param {SessionTimeoutRef} ref SessionTimeout ref.
 *
 * @returns {SessionTimeoutHooks} SessionTimeout hooks.
 */
export default function useSessionTimeoutHooks(_props: SessionTimeoutProps, ref: ForwardedRef<SessionTimeoutRef>): SessionTimeoutHooks {
  const loadingScreen = useLoadingScreen();

  const getUser = useSwrMutation(getUserFetcher.key, getUserFetcher.fetch);

  const signout = useSwrMutation(signoutFetcher.key, signoutFetcher.fetch, {
    onSuccess: () => {
      authenticator.redirectSignin();
    },

    onError: () => {
      loadingScreen.hide();
    },
  });

  // * Call signout
  const onRevoke = useCallback<DialogSessionProps['onRevoke']>(async () => {
    loadingScreen.show(1);
    await signout.trigger();
  }, [loadingScreen, signout]);

  // * Call get user to extend session
  const onExtend = useCallback<DialogSessionProps['onExtend']>(async () => {
    using _ = loadingScreen.show();
    await getUser.trigger();
  }, [loadingScreen, getUser]);

  useImperativeHandle(ref, () => ({}));

  return useMemo<SessionTimeoutHooks>(
    () => ({
      refs: {},

      states: {},

      events: {
        onRevoke,
        onExtend,
      },
    }),
    [onRevoke, onExtend],
  );
}
